import axios, { AxiosInstance } from 'axios';
// import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase-client'; // Import from the single source of truth

// Type definitions (Consider moving to a types file if they grow)
type VitalSign = {
  id: string;
  name: string;
  value: string;
  unit: string;
  timestamp: string;
};

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'; // Use environment variable or default

// --- API Client (Axios based) ---
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    // Add other default headers if needed (e.g., Authorization)
  },
});

// Add Authorization header dynamically (Example: Get token from storage)
apiClient.interceptors.request.use(async (config) => {
  // Example: Get token from auth context or local storage
  // const token = getAuthToken(); 
  // if (token) {
  //   config.headers.Authorization = `Bearer ${token}`;
  // }
  // Temporary: Simulate getting Supabase token for requests
  if (supabase) { // Ensure supabase client is initialized
    const session = await supabase.auth.getSession();
    if (session?.data?.session?.access_token) {
      config.headers.Authorization = `Bearer ${session.data.session.access_token}`;
    }
  }
  return config;
});

// --- API Functions ---

// Example: Fetch vital signs (adjust endpoint and types as needed)
const getVitalSigns = async (patientId: string): Promise<VitalSign[]> => {
  try {
    // Use Supabase for this example, adjust if using a different backend
    if (!supabase) {
      console.warn('Supabase client not initialized, returning mock data.');
      // Return mock data if Supabase isn't available
      return [
        { id: '1', name: 'Heart Rate', value: '72', unit: 'bpm', timestamp: new Date().toISOString() },
        { id: '2', name: 'Blood Pressure', value: '120/80', unit: 'mmHg', timestamp: new Date().toISOString() },
      ];
    }
    const { data, error } = await supabase
      .from('vital_signs') // Replace with your actual table name
      .select('*')
      .eq('patient_id', patientId) // Filter by patient ID
      .order('timestamp', { ascending: false });

    if (error) {
      console.error('Error fetching vital signs:', error);
      throw error;
    }
    return data as VitalSign[]; // Cast to your type
  } catch (error) {
    console.error('API Error fetching vital signs:', error);
    // Handle errors appropriately (e.g., return empty array, show error message)
    return []; // Return empty array on error
  }
};

// Export the configured clients and functions
export { supabase, getVitalSigns };
export default apiClient; // Export Axios instance as default

// --- Potential Future API Functions (Examples) ---
/*
export const getAppointments = async (params) => {
  const response = await apiClient.get('/appointments', { params });
  return response.data;
};

export const createAppointment = async (data) => {
  const response = await apiClient.post('/appointments', data);
  return response.data;
};

export const getUserProfile = async () => {
  const response = await apiClient.get('/profile');
  return response.data;
};
*/ 